import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  WidgetCopyJson,
  WidgetThemeJson,
} from "@chat-yai/shared/admin-types";
import { widgetSettingsApi } from "@/api/endpoints/widgetSettings";
import { useToast } from "@/contexts/ToastContext";
import { ApiError } from "@/api/client";
import { translateError } from "@/i18n/errors";
import { Icon } from "@/components/Icon";
import { permissionTooltip } from "@/lib/rbac";
import { VoiceSection } from "./VoiceSection";

// Where the iframe preview is hosted. Defaults to the same origin so dev
// boots without extra config; production overrides via VITE_WIDGET_PREVIEW_URL.
const PREVIEW_URL =
  (import.meta.env.VITE_WIDGET_PREVIEW_URL as string | undefined) ?? "/widget-preview.html";

interface Props {
  chatbotId: string;
  canEdit: boolean;
}

const DEFAULT_THEME: WidgetThemeJson = {
  primary_color: "#2563eb",
  position: "bottom-right",
};

const DEFAULT_COPY: WidgetCopyJson = {
  title: "Chat with us",
  subtitle: "We typically reply in seconds.",
  greeting: "Hi! How can I help you today?",
  placeholder: "Type a message…",
  teaser: "Need a hand?",
  chips: [],
};

export function ChatbotWidgetTab({ chatbotId, canEdit }: Props): JSX.Element {
  const queryClient = useQueryClient();
  const toast = useToast();

  const settingsQuery = useQuery({
    queryKey: ["widget-settings", chatbotId],
    queryFn: () => widgetSettingsApi.get(chatbotId),
  });

  const [theme, setTheme] = useState<WidgetThemeJson>(DEFAULT_THEME);
  const [copy, setCopy] = useState<WidgetCopyJson>(DEFAULT_COPY);
  const [chipDraft, setChipDraft] = useState("");
  const [dirty, setDirty] = useState(false);

  // Sync remote → local when query lands.
  useEffect(() => {
    if (settingsQuery.data) {
      setTheme({ ...DEFAULT_THEME, ...settingsQuery.data.theme });
      setCopy({ ...DEFAULT_COPY, ...settingsQuery.data.copy });
      setDirty(false);
    }
  }, [settingsQuery.data]);

  const saveMutation = useMutation({
    mutationFn: () => widgetSettingsApi.update(chatbotId, { theme, copy }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["widget-settings", chatbotId] });
      toast.push("success", "Widget updated.");
      setDirty(false);
    },
    onError: (err) => {
      toast.push("error", translateError(err instanceof ApiError ? err.code : undefined));
    },
  });

  const updateTheme = <K extends keyof WidgetThemeJson>(
    key: K,
    value: WidgetThemeJson[K],
  ): void => {
    setTheme((t) => ({ ...t, [key]: value }));
    setDirty(true);
  };
  const updateCopy = <K extends keyof WidgetCopyJson>(
    key: K,
    value: WidgetCopyJson[K],
  ): void => {
    setCopy((c) => ({ ...c, [key]: value }));
    setDirty(true);
  };

  const addChip = (): void => {
    const v = chipDraft.trim();
    if (!v) return;
    if (copy.chips.includes(v)) return;
    if (copy.chips.length >= 6) {
      toast.push("error", "Up to 6 quick replies.");
      return;
    }
    updateCopy("chips", [...copy.chips, v]);
    setChipDraft("");
  };
  const removeChip = (idx: number): void => {
    updateCopy(
      "chips",
      copy.chips.filter((_, i) => i !== idx),
    );
  };

  return (
    <>
    <div className="col-2" style={{ marginTop: 16, alignItems: "flex-start" }}>
      <div className="card" style={{ padding: 16 }}>
        <div className="sect-head">
          <h3>Theme</h3>
        </div>
        <div className="field-group">
          <label>Primary colour</label>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input
              type="color"
              value={theme.primary_color}
              disabled={!canEdit}
              onChange={(e) => updateTheme("primary_color", e.target.value)}
              data-testid="widget-primary-color"
              style={{ width: 48, height: 32, padding: 0, border: 0, background: "transparent" }}
            />
            <input
              className="field-input mono"
              style={{ flex: 1 }}
              value={theme.primary_color}
              disabled={!canEdit}
              onChange={(e) => updateTheme("primary_color", e.target.value)}
            />
          </div>
        </div>
        <div className="field-group">
          <label>Launcher position</label>
          <div className="seg" role="radiogroup" aria-label="Launcher position">
            <button
              type="button"
              role="radio"
              aria-checked={theme.position === "bottom-right"}
              className={theme.position === "bottom-right" ? "active" : ""}
              disabled={!canEdit}
              onClick={() => updateTheme("position", "bottom-right")}
              data-testid="widget-position-right"
            >
              Bottom right
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={theme.position === "bottom-left"}
              className={theme.position === "bottom-left" ? "active" : ""}
              disabled={!canEdit}
              onClick={() => updateTheme("position", "bottom-left")}
              data-testid="widget-position-left"
            >
              Bottom left
            </button>
          </div>
        </div>

        <div className="sect-head" style={{ marginTop: 18 }}>
          <h3>Copy</h3>
        </div>
        <div className="field-group">
          <label>Bot name (title)</label>
          <input
            className="field-input"
            value={copy.title}
            disabled={!canEdit}
            onChange={(e) => updateCopy("title", e.target.value)}
            data-testid="widget-title"
          />
        </div>
        <div className="field-group">
          <label>Subtitle</label>
          <input
            className="field-input"
            value={copy.subtitle}
            disabled={!canEdit}
            onChange={(e) => updateCopy("subtitle", e.target.value)}
          />
        </div>
        <div className="field-group">
          <label>Welcome message</label>
          <textarea
            className="field-input"
            rows={3}
            value={copy.greeting}
            disabled={!canEdit}
            onChange={(e) => updateCopy("greeting", e.target.value)}
            data-testid="widget-greeting"
          />
        </div>
        <div className="field-group">
          <label>Input placeholder</label>
          <input
            className="field-input"
            value={copy.placeholder}
            disabled={!canEdit}
            onChange={(e) => updateCopy("placeholder", e.target.value)}
          />
        </div>
        <div className="field-group">
          <label>Quick replies (chips)</label>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
            {copy.chips.map((c, i) => (
              <span
                key={i}
                className="chip"
                style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
                data-testid={`widget-chip-${i}`}
              >
                {c}
                {canEdit ? (
                  <button
                    className="icon-btn"
                    aria-label={`Remove ${c}`}
                    onClick={() => removeChip(i)}
                    style={{ width: 14, height: 14 }}
                  >
                    <Icon name="x" size={10} />
                  </button>
                ) : null}
              </span>
            ))}
            {copy.chips.length === 0 ? (
              <span className="help">No quick replies yet.</span>
            ) : null}
          </div>
          {canEdit ? (
            <div style={{ display: "flex", gap: 6 }}>
              <input
                className="field-input"
                placeholder="e.g. Book a tour"
                value={chipDraft}
                onChange={(e) => setChipDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addChip();
                  }
                }}
                data-testid="widget-chip-input"
              />
              <button
                type="button"
                className="btn btn-outline btn-sm"
                onClick={addChip}
                data-testid="widget-add-chip"
              >
                Add
              </button>
            </div>
          ) : null}
        </div>

        <div style={{ marginTop: 16, display: "flex", justifyContent: "flex-end", gap: 8 }}>
          {!canEdit ? (
            <span className="help">{permissionTooltip("write")}</span>
          ) : null}
          <button
            className="btn btn-primary btn-sm"
            disabled={!canEdit || !dirty || saveMutation.isPending}
            onClick={() => saveMutation.mutate()}
            data-testid="widget-save"
          >
            {saveMutation.isPending ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>

      <div className="card" style={{ padding: 16 }}>
        <div className="sect-head">
          <h3>Live preview</h3>
          <span className="help">Updates as you type</span>
        </div>
        <p className="help" style={{ marginTop: 0 }}>
          Hosted demo widget rendered inside an iframe. Theme and copy are
          pushed via <span className="mono">postMessage</span>, so the
          preview matches what visitors will see on your site.
        </p>
        <WidgetPreview theme={theme} copy={copy} chatbotId={chatbotId} />
      </div>
    </div>
    <VoiceSection chatbotId={chatbotId} canEdit={canEdit} />
    </>
  );
}

// Iframe-based preview (WP-C, U-C3). The iframe loads /widget-preview.html
// (or VITE_WIDGET_PREVIEW_URL) and listens for {type:"theme"|"copy"|"snapshot"}
// postMessage payloads. We push the latest snapshot whenever theme/copy
// change OR when the iframe announces it is ready.
function WidgetPreview({
  theme,
  copy,
  chatbotId,
}: {
  theme: WidgetThemeJson;
  copy: WidgetCopyJson;
  chatbotId: string;
}): JSX.Element {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    function onMessage(evt: MessageEvent): void {
      if (!evt.data || typeof evt.data !== "object") return;
      if (evt.data.type === "preview-ready") setReady(true);
    }
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  // Push snapshot whenever the inputs change (and once we know the iframe is
  // ready). Origin "*" is intentional in dev because the preview is served
  // from the same origin as the admin; the production preview will check
  // event.origin against its allow-list.
  useEffect(() => {
    if (!ready) return;
    const win = iframeRef.current?.contentWindow;
    if (!win) return;
    win.postMessage({ type: "snapshot", payload: { theme, copy } }, "*");
  }, [ready, theme, copy]);

  const url = `${PREVIEW_URL}?chatbot=${encodeURIComponent(chatbotId)}&token=demo`;
  return (
    <iframe
      ref={iframeRef}
      src={url}
      title="Widget preview"
      className="widget-preview-iframe"
      data-testid="widget-preview"
      // sandbox keeps the demo isolated; allow-scripts lets it react to
      // postMessage. We deliberately do NOT add allow-same-origin so the
      // iframe can never read storage / cookies on the admin origin.
      sandbox="allow-scripts"
    />
  );
}
